'use client';

import React from 'react';
import { Form, Input, Button, Card, Typography, Checkbox, Space, Divider, App } from 'antd';
import { UserOutlined, LockOutlined, GlobalOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import Image from 'next/image';
import Link from 'next/link';

import { callApi } from '@/utils/httpClient';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/stores/authStore';

const { Title, Text } = Typography;

export default function LoginPage() {
  const [loading, setLoading] = React.useState(false);
  const router = useRouter();
  const { message } = App.useApp();

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const response = await callApi('/auth/login', {
        method: 'POST',
        body: JSON.stringify(values),
      });

      const result = await response.json();

      if (result.success) {
        message.success(result.message || 'Đăng nhập thành công');
        const { user, accessToken } = result.data;
        useAuthStore.getState().setUser(user, accessToken);
        // Chuyển hướng sau khi lưu xong Session thực tế vào Browser
        router.push('/dashboard');
      } else {
        message.error(result.message || 'Tài khoản hoặc mật khẩu không chính xác');
      }
    } catch (error) {
      console.error('Lỗi khi đăng nhập:', error);
      message.error('Hệ thống gặp sự cố, vui lòng thử lại sau');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 overflow-hidden relative">
      <div className="absolute top-0 right-0 p-8">
        <Space size="large">
          <Link href="/help" className="text-gray-400 hover:text-primary transition-colors"><QuestionCircleOutlined className="mr-1" /> Trợ giúp</Link>
          <Text className="text-gray-300">|</Text>
          <Text className="text-gray-400 cursor-pointer hover:text-primary transition-colors"><GlobalOutlined className="mr-1" /> Tiếng Việt</Text>
        </Space>
      </div>

      <Card className="w-full max-w-md shadow-2xl border-none rounded-3xl p-6 relative z-10 mx-4 backdrop-blur-sm bg-white/95">
        <div className="flex flex-col items-center mb-10 text-center">
          <div className="mb-6">
               <Link href="/">
                <Image 
                  src="/logo.png" 
                  alt="eSIM NhanHoa" 
                  width={220} 
                  height={100} 
                  priority 
                  loading="eager"
                  className="hover:scale-105 transition-all duration-300 cursor-pointer brightness-105"
                  style={{ width: 'auto', height: 'auto' }}
                />
               </Link>
          </div>
          <Title level={2} className="m-0 text-primary font-extrabold tracking-tight">ĐĂNG NHẬP</Title>
          <Text type="secondary" className="text-lg font-medium opacity-70">Sẵn sàng kết nối 200+ quốc gia</Text>
        </div>

        <Form
          name="login_form"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          layout="vertical"
          size="large"
          className="mt-4"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: 'Nhập Email hoặc Mã khách hàng!' }]}
          >
            <Input 
              prefix={<UserOutlined className="text-gray-300 mr-2" />} 
              placeholder="Email / Mã khách hàng" 
              className="h-13 rounded-xl border-gray-100 focus:border-primary transition-all overflow-hidden"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Nhập mật khẩu truy cập!' }]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-300 mr-2" />}
              placeholder="Mật khẩu bảo mật"
              className="h-13 rounded-xl border-gray-100 focus:border-primary transition-all overflow-hidden"
            />
          </Form.Item>

          <div className="flex justify-between items-center mb-8 px-1">
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox className="text-gray-500 font-medium tracking-tight">Lưu thông tin</Checkbox>
            </Form.Item>
            <Link href="/forgot-password" title="Quên mật khẩu?" className="text-secondary font-bold hover:text-primary transition-all underline decoration-transparent hover:decoration-secondary">
              Quên mật khẩu?
            </Link>
          </div>

          <Form.Item className="mb-0 mt-8">
            <Button 
                type="primary" 
                htmlType="submit" 
                block 
                loading={loading}
                className="h-15 rounded-xl text-lg font-bold shadow-xl shadow-primary/20 bg-primary hover:bg-blue-800 border-none transition-all transform hover:-translate-y-0.5 active:translate-y-0"
            >
              ĐĂNG NHẬP NGAY
            </Button>
          </Form.Item>


        </Form>
      </Card>

      <div className="absolute inset-x-0 bottom-0 text-center p-6 text-gray-400 text-xs">
          Copyright © 2026 eSIM NhanHoa. Powered by Công ty Cổ phần ESIMONE VIETNAM.
      </div>
    </div>
  );
}
